import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, setPageMeta } from "@/lib/api";
import { Reveal } from "@/components/Motion";

const TABS = [
  ["submit", "SUBMIT A CLUB", "club-submission"],
  ["claim", "CLAIM A CLUB", "claim-club"],
  ["correct", "CORRECT A LISTING", "correction"],
];

export default function SubmitClub() {
  const [params] = useSearchParams();
  const initialTab = params.get("tab") || "submit";
  const [tab, setTab] = useState(TABS.some(([t]) => t === initialTab) ? initialTab : "submit");
  const [form, setForm] = useState({
    name: "", email: "", club: params.get("club") || "", city: "", state: "",
    instagram: "", clubWebsite: "", schedule: "", details: "", website: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setPageMeta("Submit or Claim a Run Club | Ninja Run Club", "Add your run club to the national directory, claim an existing profile, or correct a listing. Every submission is human-reviewed.");
  }, []);

  const formType = TABS.find(([t]) => t === tab)[2];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { name, email, website, ...payload } = form;
      const res = await api.post(`/forms/${formType}`, { name, email, website, payload: { ...payload, tab } });
      toast.success(res.data.message);
      setDone(true);
    } catch {
      toast.error("Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200";

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <Reveal>
          <p className="font-mono-label text-white/40">THE DIRECTORY GROWS WITH YOU</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">PUT YOUR CLUB ON THE MAP.</h1>
          <p className="text-white/50 text-sm max-w-2xl mt-4">
            Free, forever, never pay-to-play. A listing puts your club in front of runners searching by city and meet day,
            with your schedule, pace culture, and socials shown exactly the way you want them. Every submission is reviewed
            by a human before it goes live — we never auto-publish, and we never invent details.
          </p>
        </Reveal>
      </header>

      <section className="px-5 lg:px-10 py-10 border-b border-white/10" data-testid="submit-benefits">
        <h2 className="font-mono-label text-white/50">WHAT YOUR CLUB GETS</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-5">
          {[
            ["FOUND BY RUNNERS", "A profile in the national directory and your city hub, searchable by city, day, and tags like beginner-friendly."],
            ["A VERIFIED BADGE", "Claim your profile to get the verified mark, a last-checked date, and the ability to keep details current yourself."],
            ["A SHOT AT THE RANKINGS", "Every club is scored on the same public rubric — pace honesty, no-drop mechanics, first-timer experience. No pay-to-play."],
          ].map(([title, desc]) => (
            <div key={title} className="bg-night p-6">
              <h3 className="font-mono-label text-white/40">{title}</h3>
              <p className="text-white/60 text-sm mt-3">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="px-5 lg:px-10 py-10 max-w-3xl">
        <div className="flex gap-2 mb-10 flex-wrap" data-testid="submit-tabs">
          {TABS.map(([key, labelText]) => (
            <button
              key={key}
              data-testid={`submit-tab-${key}`}
              onClick={() => { setTab(key); setDone(false); }}
              className={`font-mono-label px-5 py-3 border transition-colors duration-200 ${tab === key ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"}`}
            >
              {labelText}
            </button>
          ))}
        </div>

        {done ? (
          <div className="border border-white p-10" data-testid="submit-success">
            <p className="font-display text-3xl">IN THE QUEUE.</p>
            <p className="text-white/60 text-sm mt-3">A moderator will verify the details against public sources before anything goes live. Thanks for building the map.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid={`submit-form-${tab}`}>
            <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} aria-hidden="true" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <input data-testid="submit-name-input" required placeholder="YOUR NAME" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
              <input data-testid="submit-email-input" required type="email" placeholder="YOUR EMAIL" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
              <input data-testid="submit-club-input" required placeholder="CLUB NAME" value={form.club} onChange={(e) => setForm({ ...form, club: e.target.value })} className={input} />
              <input data-testid="submit-city-input" placeholder="CITY, STATE" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={input} />
            </div>
            <details className="pt-1" data-testid="submit-optional-details">
              <summary className="font-mono-label text-white/50 cursor-pointer list-none hover:text-white transition-colors duration-200">
                + OPTIONAL DETAILS (SPEEDS UP REVIEW)
              </summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5">
                <input data-testid="submit-instagram-input" placeholder="INSTAGRAM HANDLE" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className={input} />
                <input data-testid="submit-website-input" placeholder="CLUB WEBSITE" value={form.clubWebsite} onChange={(e) => setForm({ ...form, clubWebsite: e.target.value })} className={input} />
                {tab === "submit" && (
                  <input data-testid="submit-schedule-input" placeholder="TYPICAL SCHEDULE (E.G. SATURDAYS 7AM)" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} className={`${input} sm:col-span-2`} />
                )}
              </div>
            </details>
            <textarea
              data-testid="submit-details-input"
              rows={4}
              placeholder={tab === "correct" ? "WHAT NEEDS FIXING? INCLUDE A PUBLIC SOURCE URL IF YOU CAN." : tab === "claim" ? "HOW ARE YOU CONNECTED TO THIS CLUB? (FOUNDER, LEADER, ORGANIZER...)" : "TELL US ABOUT THE CLUB: VIBE, PACES, WHO IT IS FOR."}
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
              className="w-full bg-transparent border border-white/30 p-4 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200"
            />
            <button type="submit" disabled={loading} data-testid="submit-form-button" className="btn-invert bg-white text-ink font-mono-label px-10 py-4 border border-white hover:bg-transparent hover:text-white disabled:opacity-50">
              {loading ? "SENDING..." : "SEND FOR REVIEW"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
